import { faPlus, faTools } from "@fortawesome/pro-regular-svg-icons";
import React, { useCallback, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { FlowToolFlowSelectionModal } from "src/agents/AgentVersionPage/AgentToolsEditor/FlowToolFlowSelectionModal";
import { useSideDrawerCoordinator } from "src/agents/base/SideDrawer";
import { ToolCard, ToolType } from "src/agents/base/ToolCard";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { Flow, FlowVersion, Schema } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import {
  AgentVersionConfigurationInputToolsInner,
  MetadataOverlay,
} from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";

type AgentToolsEditorProps = {
  immutable: boolean;
};

/**
 * Build metadata_overlay from I/O schema, preserving existing descriptions
 * when properties still exist in the new schema.
 */
export const buildMetadataOverlay = (
  schema?: Schema,
  existingOverlay?: MetadataOverlay,
): MetadataOverlay => {
  const properties = schema?.properties ?? {};

  const overlayProperties: Record<string, { description?: string }> = {};

  for (const propName of Object.keys(properties)) {
    const existingDescription =
      existingOverlay?.properties?.[propName]?.description;
    overlayProperties[propName] = {
      description: existingDescription ?? "",
    };
  }

  return { properties: overlayProperties };
};

/**
 * Build a new tool from a selected flow and version.
 */
export const buildNewTool = (
  flow: Flow,
  version: FlowVersion,
): AgentVersionConfigurationInputToolsInner => {
  const inputMetadataOverlay = buildMetadataOverlay(version.input_schema);
  const outputMetadataOverlay = buildMetadataOverlay(version.output_schema);

  return {
    type: "flow",
    name: flow.name,
    description: flow.meta.description || undefined,
    flow_slug: flow.slug,
    flow_version_id: version.id,
    output_type: "json",
    input_schema_metadata_overlay: inputMetadataOverlay,
    output_schema_metadata_overlay: outputMetadataOverlay,
    input_schema: {},
    output_schema: {},
  };
};

/**
 * Update an existing tool with a new flow/version selection.
 */
export const buildUpdatedTool = (
  existingTool: AgentVersionConfigurationInputToolsInner,
  flow: Flow,
  version: FlowVersion,
): AgentVersionConfigurationInputToolsInner => {
  const inputMetadataOverlay = buildMetadataOverlay(
    version.input_schema,
    existingTool.input_schema_metadata_overlay,
  );
  const outputMetadataOverlay = buildMetadataOverlay(
    version.output_schema,
    existingTool.output_schema_metadata_overlay,
  );

  return {
    ...existingTool,
    flow_slug: flow.slug,
    flow_version_id: version.id,
    input_schema_metadata_overlay: inputMetadataOverlay,
    output_schema_metadata_overlay: outputMetadataOverlay,
  };
};

export const AgentToolsEditor: React.FC<AgentToolsEditorProps> = ({
  immutable,
}) => {
  const { control } = useFormContext<AgentVersionConfigurationInput>();
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "tools",
  });

  // Drawer coordinator
  const { requestOpen, requestClose, openDrawer } = useSideDrawerCoordinator();

  // Flow selection modal state
  const [flowModalState, setFlowModalState] = useState<
    { type: "new" } | { type: "existing"; index: number } | null
  >(null);

  // Open the config drawer for a tool
  const openToolConfigDrawer = useCallback(
    (tool: AgentVersionConfigurationInputToolsInner, index: number) => {
      requestOpen({
        type: "flowToolConfig",
        data: {
          tool,
          toolIndex: index,
          immutable,
          onToolChange: (updatedTool) => update(index, updatedTool),
          onRequestChangeFlow: () =>
            setFlowModalState({ type: "existing", index }),
        },
      });
    },
    [immutable, update, requestOpen],
  );

  // Handle flow/version selection from modal
  const handleFlowVersionSelected = useCallback(
    (flow: Flow, version: FlowVersion) => {
      if (flowModalState?.type === "new") {
        const newTool = buildNewTool(flow, version);
        append(newTool);
        openToolConfigDrawer(newTool, fields.length);
      } else if (flowModalState?.type === "existing") {
        const existingTool = fields[flowModalState.index];
        const updatedTool = buildUpdatedTool(existingTool, flow, version);
        update(flowModalState.index, updatedTool);
        openToolConfigDrawer(updatedTool, flowModalState.index);
      }
      setFlowModalState(null);
    },
    [flowModalState, append, fields, update, openToolConfigDrawer],
  );

  // Handle clicking on a tool card to edit it
  const handleEditTool = useCallback(
    (index: number) => {
      const tool = fields[index];
      openToolConfigDrawer(tool, index);
    },
    [fields, openToolConfigDrawer],
  );

  // Handle deleting a tool
  const handleDeleteTool = useCallback(
    (index: number) => {
      // Temporarily always close the drawer when deleting a tool
      // to avoid stale callback issues
      requestClose();
      remove(index);
    },
    [requestClose, remove],
  );

  // Handle clicking "+ Add tool" button
  const handleAddTool = useCallback(() => {
    requestOpen({
      type: "addTool",
      data: {
        onAddFlowTool: () => setFlowModalState({ type: "new" }),
      },
    });
  }, [requestOpen]);

  // Handle flow selection modal close
  const handleFlowModalClose = useCallback(() => {
    setFlowModalState(null);
  }, []);

  return (
    <>
      <div>
        {fields.length === 0 ? (
          <EmptyState
            actionButton={
              immutable
                ? undefined
                : {
                    text: "Add tool",
                    icon: faPlus,
                    onClick: handleAddTool,
                  }
            }
            description="Add tools for the agent to use when processing requests"
            headline="No tools configured"
            icon={faTools}
          />
        ) : (
          <div className="relative ml-4 rounded-lg bg-gray-50 p-5 pr-8">
            {!immutable && (
              <div className="absolute -top-10 right-0">
                <Button
                  dataLoc="add-tool-button"
                  size="sm"
                  variant="secondary"
                  onClick={handleAddTool}
                >
                  + Add tool
                </Button>
              </div>
            )}
            <div className="flex flex-col gap-4">
              {fields.map((field, index) => {
                const isSelected =
                  openDrawer?.type === "flowToolConfig" &&
                  openDrawer.data?.toolIndex === index;
                return (
                  <ToolCard
                    key={field.id}
                    dataLoc={`tool-card-${index}`}
                    description={field.description}
                    name={field.name}
                    selected={isSelected}
                    type={(field.type as ToolType) || "flow"}
                    onClick={() => handleEditTool(index)}
                    onDelete={
                      immutable ? undefined : () => handleDeleteTool(index)
                    }
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Flow selection modal - rendered here so AgentToolsEditor owns its state */}
      <FlowToolFlowSelectionModal
        open={flowModalState !== null}
        onClose={handleFlowModalClose}
        onSelect={handleFlowVersionSelected}
      />
    </>
  );
};
